import {
  Box, Grid, Link,
} from '@mui/material';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import HeaderMenuSettingsModal from '../shared/HeaderMenuSettingsModal';
import BlockData from '../../../../models/BlockData';
import BlockProps from '../../../../models/BlockProps';
import LinkData from '../../../../models/LinkData';
import BlockHeaderLogoMenuData from './BlockHeaderLogoMenuData';
import CloseButton from '../../../Buttons/Close';
import Utils from '../../../../utils/Utils';
import styles from './BlockHeaderLogoMenu.module.css';
import HeaderMenu from '../shared/HeaderMenu';
import HamburgerMenuButton from '../../../Buttons/HamburgerMenuButton';
import BlockComponentImage from '../../../../models/BlockComponentValues/BlockComponentImage';
import ImageSettingsModal from '../../../Modals/ImageSettingsModal/ImageSettingsModal';
import CroppedImage from '../../../CroppedImage';
import { Size } from '../../../../models/BlockComponentSettings/ImageSettings';
import { ImageAspectRatio, ImageOrientation } from '../../BlockImageList/ImageSettings';
import useGetBackground from '../../../../hooks/UseGetBackground';
import BackGroundImageModal from '../../../Modals/ImageModal/BackGroundImageModal';
import useParallaxScript from '../../../../hooks/useParallaxScript';
import config from '../../../../config';
import { BlockSeparator } from '../../../../models/BlockSettings';
import AutoCloseMenus from '../shared/AutoCloseMenus';
import ImageEnlargeModal from '../../../Images/ImageEnlargeModal';

export interface BlockHeaderLogoMenuProps extends BlockProps {
  blockData: BlockData[],
  backgroundColor?: string,
}

function BlockHeaderLogoMenu({
  data,
  isEditing,
  isTemplate,
  editHandler,
  blockData,
  showImageModal,
  onCloseImageModal,
  backgroundColor,
}: BlockHeaderLogoMenuProps) {
  const targetModalId = uuid();

  const [props, setProps] = useState<BlockHeaderLogoMenuData>(data.data as BlockHeaderLogoMenuData);

  useEffect(() => {
    setProps(data.data as BlockHeaderLogoMenuData);
  }, [data.data, data.data?.settings]);

  const hamburgerCheckbox = useRef(null);
  const [open, setOpen] = useState(false);

  const [backgroundColorState, setBackgroundColorState] = useState<string | undefined>(backgroundColor);

  const handleOpen = useCallback(() => {
    if (isEditing && !isTemplate) {
      setOpen(true);
    }
  }, [isEditing, isTemplate]);
  const handleClose = () => setOpen(false);

  const handleMenuDelete = useCallback(() => {
    props.menus = undefined;
    editHandler(data.id, props);
  }, [data.id, editHandler, props]);

  const hamburgerButton = useMemo(() => (
    <Grid
      id="logoHeaderHamburger"
      className="logoHeaderHamburgerButton previewResponsiveShow"
      item
      xs="auto"
    >
      {isEditing || isTemplate ? (
        <HamburgerMenuButton />
      ) : (
        <label htmlFor={`hamburger-${data.id}`}>
          <HamburgerMenuButton />
        </label>
      )}
    </Grid>
  ), [isEditing, isTemplate]);

  const hamburgerMenu = useMemo(() => {
    if (props.menus && props.menus.length > 0) {
      return (
        <Grid
          id="logoHeaderHamburgerMenu"
          className="logoHeaderHamburgerMenu previewResponsiveShow"
          container
          justifyContent="center"
          alignItems="center"
          direction="column"
          sx={{
            p: 1.5,
            backgroundColor: props.url?.solidColor ?? backgroundColor,
          }}
        >
          <Grid item xs={12} sx={{ width: '100%' }}>
            <HeaderMenu
              menus={props.menus}
              headerSettings={props.settings}
              isEditing={isEditing}
              direction="column"
              handleMenuDelete={handleMenuDelete}
              handleOpen={handleOpen}
            />
          </Grid>
        </Grid>
      );
    }
    return null;
  }, [handleMenuDelete, handleOpen, isEditing, props.menus, props.settings]);

  const menuItems = useMemo(() => {
    if (props.menus && props.menus?.length > 0) {
      return (
        <Grid id="logoHeaderMenu" className="logoHeaderMenu" sx={{ width: 'auto' }} container justifyContent="center" alignItems="center">
          <HeaderMenu
            showSeparators
            menus={props.menus}
            headerSettings={props.settings}
            isEditing={isEditing}
            handleMenuDelete={handleMenuDelete}
            handleOpen={handleOpen}
          />
        </Grid>
      );
    }
    return [];
  }, [props.menus, props.settings, isEditing, handleMenuDelete, handleOpen]);

  const [innerBoxSize, setInnerBoxSize] = useState<{ width: string, height: string }>({ width: '89px', height: '50px' });

  const calcInnerBoxSize = (frameSize: Size) => {
    let newWidth = 0;
    if (props.logo.settings.fixedAspect) {
      switch (props.logo.settings.orientation) {
        case ImageOrientation.Portrait:
        case ImageOrientation.PortraitOval:
          if (props.logo.settings.aspectRatio === ImageAspectRatio.SixteenByNine) {
            newWidth = (9 / 16) * 50;
          } else if (props.logo.settings.aspectRatio === ImageAspectRatio.FourByThree) {
            newWidth = (3 / 4) * 50;
          }
          break;
        case ImageOrientation.Square:
        case ImageOrientation.Circle:
          newWidth = 50;
          break;
        case ImageOrientation.Landscape:
        case ImageOrientation.LandscapeOval:
        default:
          if (props.logo.settings.aspectRatio === ImageAspectRatio.SixteenByNine) {
            newWidth = (16 / 9) * 50;
          } else if (props.logo.settings.aspectRatio === ImageAspectRatio.FourByThree) {
            newWidth = (4 / 3) * 50;
          }
          break;
      }
    } else {
      newWidth = (frameSize.width / frameSize.height) * 50;
    }

    const newHeight = '50px';
    setInnerBoxSize({ width: `${newWidth}px`, height: newHeight });
  };

  // effects
  useEffect(() => {
    if (!props.settings?.separator) {
      setProps({ ...props, settings: { ...props.settings, separator: BlockSeparator.NONE } });
    }
  }, [props]);

  useEffect(() => {
    if (props.logo) {
      calcInnerBoxSize(props.logo.settings.frameSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.logo]);

  useEffect(() => {
    if (isEditing && hamburgerCheckbox.current?.checked) {
      hamburgerCheckbox.current.checked = false;
    }
  }, [isEditing]);

  useEffect(() => {
    setBackgroundColorState(props.url?.solidColor ?? backgroundColor);
  }, [props.url?.solidColor, backgroundColor]);

  useEffect(() => {
    AutoCloseMenus();
  }, [data]);

  const elementUuid = uuid();
  useParallaxScript(props.url?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  return (
    <>
      <Grid
        id={elementUuid}
        container
        spacing={0}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        className={`${styles.header} ${!props.isFree ? 'blockHeaderType' : ''}`}
        width="100%"
        sx={{
          overflow: 'hidden',
          ...useGetBackground(
            backgroundColorState,
            props.url?.value,
            props.url?.settings?.scale,
            props.url?.settings?.scaleBool,
          ),
          containerType: 'inline-size',
          backgroundPosition: 'center 0px',
        }}
      >
        {(isEditing && !isTemplate) && (
          <BackGroundImageModal
            backgroundImage={props.url}
            defaultBackgroundColor={backgroundColor}
            isShowModal={showImageModal}
            onCloseModal={onCloseImageModal}
            handleOnConfirm={(url: BlockComponentImage) => {
              const tempBg = { ...props, url: { ...url } };
              setProps(tempBg);
              editHandler(data.id, tempBg);
            }}
          />
        )}
        <Grid container justifyContent="space-between" alignItems="center" sx={{ p: 1.5 }}>
          {props.logo ? (
            <Grid item xs={6} sx={Utils.hoverBorder(isEditing)}>
              <CloseButton
                show={isEditing}
                updateHandler={() => {
                  props.logo = undefined;
                  editHandler(data.id, props);
                }}
              />
              <Box
                sx={{
                  maxHeight: '50px',
                  height: '50px',
                  width: 'auto',
                  overflow: 'hidden',
                }}
              >
                <ImageSettingsModal
                  isEditing={isEditing}
                  blockData={blockData}
                  image={props.logo}
                  hasLogo
                  disabled={isTemplate || !isEditing}
                  onConfirm={(newImage: BlockComponentImage) => {
                    calcInnerBoxSize(newImage.settings.frameSize);
                    props.logo = newImage;
                    editHandler(data.id, props);
                  }}
                >
                  <Box
                    sx={{ width: innerBoxSize.width || '100px', height: innerBoxSize.height }}
                  >
                    {(!isEditing) ? (
                      <Link
                        style={{ cursor: props?.logo?.link?.linkData?.url ? 'pointer' : 'default' }}
                        target={props?.logo?.link?.linkData?.openType}
                        href={props?.logo?.link?.linkData?.url || undefined}
                        rel="noreferrer"
                      >
                        <CroppedImage
                          image={props.logo}
                          isEditing={isEditing}
                          targetModalId={targetModalId}
                          settings={props.logo.settings}
                        />
                      </Link>
                    ) : (
                      <CroppedImage
                        image={props.logo}
                        isEditing={isEditing}
                        targetModalId={targetModalId}
                        settings={props.logo.settings}
                      />
                    )}
                  </Box>
                </ImageSettingsModal>
              </Box>
            </Grid>
          ) : (
            <Grid item />
          )}
          <Grid item xs={6} sx={{ paddingLeft: props.logo ? 1.5 : 'none' }}>
            <Box
              id="logoMenuWrapper"
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}
            >
              {hamburgerButton}
              {menuItems}
            </Box>
          </Grid>

        </Grid>

        <input
          style={{ display: 'none' }}
          type="checkbox"
          id={`hamburger-${data.id}`}
          data-target={`hamburger-logo-menu-${data.id}`}
          className="hamburger-checkbox"
        />
        <Box
          id={`hamburger-logo-menu-${data.id}`}
          className="responsive-menu block-header-logo-menu"
          sx={{ width: '100%' }}
          style={{
            pointerEvents: (isEditing || isTemplate) ? 'none' : 'auto',
          }}
        >
          {hamburgerMenu}
        </Box>

        <HeaderMenuSettingsModal
          hasJustifyOptions={false}
          menuItems={props.menus ?? []}
          settings={props.settings}
          open={open}
          closeHandler={(newItems?: LinkData[]) => {
            if (newItems) {
              props.menus = newItems;
              editHandler(data.id, props);
            }
            handleClose();
          }}
          updateHandler={(settings) => {
            props.settings = settings;
          }}
          blockData={blockData}
        />

      </Grid>
      {!isEditing && (
        <Box
          sx={{
            backgroundColor: `${props.url.solidColor ?? backgroundColor} !important`,
          }}
        />
      )}

      <ImageEnlargeModal
        images={[props.logo]}
        targetModalId={targetModalId}
      />
    </>
  );
}

export default BlockHeaderLogoMenu;
